import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { Languages, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [message, setMessage] = useState("");
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get("session_id");
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      // For Payment Links without session_id, just refresh user and check plan
      if (!sessionId) {
        try {
          await refreshUser();
          // Small delay to allow webhook to process
          await new Promise(resolve => setTimeout(resolve, 2000));
          await refreshUser();
          
          // Check if user is now PRO
          if (user?.plan === "pro" || user?.plan === "lifetime") {
            setStatus("success");
            setMessage("Pagamento confirmado! Você agora é um membro PRO.");
          } else if (attempts < 5) {
            setAttempts(prev => prev + 1);
            setTimeout(checkPaymentStatus, 3000);
          } else {
            // After 5 attempts, show manual verification option
            setStatus("pending");
            setMessage("Seu pagamento está sendo processado. Faça login novamente em alguns minutos para ativar seu plano PRO.");
          }
        } catch (error) {
          console.error("Error refreshing user:", error);
          if (attempts < 3) {
            setAttempts(prev => prev + 1);
            setTimeout(checkPaymentStatus, 2000);
          } else {
            setStatus("pending");
            setMessage("Não foi possível verificar o status do pagamento. Faça login novamente em alguns minutos.");
          }
        }
        return;
      }
      
      // Original flow with session_id
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/payments/stripe/status/${sessionId}`,
          { withCredentials: true }
        );
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          setMessage("Pagamento confirmado! Você agora é um membro PRO.");
          await refreshUser();
        } else if (response.data.status === "expired") {
          setStatus("failed");
          setMessage("Sessão de pagamento expirada. Por favor, tente novamente.");
        } else {
          // Keep polling
          if (attempts < 5) {
            setAttempts(prev => prev + 1);
            setTimeout(checkPaymentStatus, 2000);
          } else {
            setStatus("failed");
            setMessage("Não foi possível confirmar o pagamento. Verifique seu email para confirmação.");
          }
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (attempts < 3) {
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus("failed");
          setMessage("Erro ao verificar pagamento. Por favor, entre em contato com o suporte.");
        }
      }
    };
    
    checkPaymentStatus();
  }, [sessionId, refreshUser, attempts, user?.plan]);
  
  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <Link to="/" className="flex items-center gap-3 mb-8">
        <Languages className="w-10 h-10 text-white" />
        <span className="text-2xl font-bold text-white font-['Outfit']">
          Tradutor em Tempo Real
        </span>
      </Link>
      
      <Card className="w-full max-w-md bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-8 pb-8 text-center space-y-6">
          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 text-white animate-spin mx-auto" />
              <h2 className="text-xl font-bold text-white">
                Verificando Pagamento...
              </h2>
              <p className="text-zinc-400">
                Por favor, aguarde enquanto confirmamos seu pagamento.
              </p>
            </>
          )}
          
          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
              <h2 className="text-xl font-bold text-white">
                Pagamento Confirmado!
              </h2>
              <p className="text-zinc-400">
                {message}
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="bg-white text-black hover:bg-zinc-200"
              >
                Ir para o Dashboard
              </Button>
            </>
          )}
          
          {status === "pending" && (
            <>
              <Loader2 className="w-16 h-16 text-yellow-500 mx-auto" />
              <h2 className="text-xl font-bold text-white">
                Pagamento em Processamento
              </h2>
              <p className="text-zinc-400">
                {message}
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={async () => {
                    setStatus("checking");
                    setAttempts(0);
                    await refreshUser();
                  }}
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800 text-white"
                >
                  Verificar Novamente
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  Ir para o Dashboard
                </Button>
              </div>
            </>
          )}
          
          {status === "failed" && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto" />
              <h2 className="text-xl font-bold text-white">
                Ops! Algo deu errado
              </h2>
              <p className="text-zinc-400">
                {message}
              </p>
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => navigate("/pricing")}
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800 text-white"
                >
                  Tentar Novamente
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-white text-black hover:bg-zinc-200"
                >
                  Ir para o Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Copyright */}
      <p className="mt-8 text-zinc-700 text-[10px] sm:text-xs text-center">
        {t("footerCopyright")}
      </p>
    </div>
  );
}
